import { createAgxForm, createFormDefinition } from '@urbanx/agx.forms';
import { isValidPhoneNumber } from '@urbanx/agx.ui-components2';
import { z } from 'zod';

import { isEmailValid } from 'helpers/emailHelper';
import { rolesWithStateAccess } from './staffPageUtils';

const staffMemberFormSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name required'),
    lastName: z.string().trim().min(1, 'Last name required'),
    title: z.string().trim().min(1, 'Job title required'),
    email: z
      .string()
      .trim()
      .min(1, 'Email required')
      .refine(isEmailValid, 'Please specify a valid email address'),
    phoneNumber: z
      .string()
      .refine(
        (phoneNumber) => !phoneNumber || isValidPhoneNumber(phoneNumber),
        'Enter a valid phone number'
      ),
    roles: z.array(z.string()).min(1, 'Role(s) required'),
    states: z.array(z.string()),
  })
  .refine(
    ({ roles, states }) =>
      !roles.some((role) => rolesWithStateAccess.includes(role)) || states.length > 0,
    { path: ['states'], message: 'State(s) required' }
  );

export type StaffMemberFormFields = z.input<typeof staffMemberFormSchema>;

const staffMemberFormDefinition = createFormDefinition({
  id: 'staff-member',
  schema: staffMemberFormSchema,
  fieldOrder: ['firstName', 'lastName', 'title', 'email', 'phoneNumber', 'roles', 'states'],
  resolveFields: ({ roles }) => ({
    firstName: { active: true, visible: true, disabled: false, required: true },
    lastName: { active: true, visible: true, disabled: false, required: true },
    title: { active: true, visible: true, disabled: false, required: true },
    email: { active: true, visible: true, disabled: false, required: true },
    roles: { active: true, visible: true, disabled: false, required: true },
    states: {
      active: roles.some((role) => rolesWithStateAccess.includes(role)),
      visible: roles.some((role) => rolesWithStateAccess.includes(role)),
      disabled: false,
      required: true,
    },
  }),
});

export const StaffMemberForm = createAgxForm(staffMemberFormDefinition);
